import { useState } from 'react';
import { getHierarchicalData } from 'assets/data/dataList';
import {
    Button,
    FieldErrorList,
    FormField,
    Input,
    Label,
    MessageModal,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    TextArea
} from 'common/components';
import {
    ACTION_ITEMS,
    SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    SUB_CATEGORY_NAME_EMPTY_ERROR,
    SUB_CATEGORY_NAME_INVALID_CHAR_ERROR,
    SUB_CATEGORY_NAME_MAX_LENGTH,
    SUB_CATEGORY_NAME_MAX_LENGTH_ERROR,
    SUB_CATEGORY_ORDER_EMPTY_ERROR,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH,
    SUB_CATEGORY_ORDER_GREATER_THAN_ZERO,
    SUB_CATEGORY_ORDER_INVALID_ERROR,
    SUB_CATEGORY_ORDER_MAX_LENGTH,
    SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
} from 'common/config';
import { ButtonLabel, ButtonVariant, EntityType, InputType, MessageType } from 'common/enums';
import { FieldError } from 'common/models';
import { getSortOrderList } from 'common/utils/data';
import { addValidationError, removeValidationError } from 'common/utils/error';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import { SubCategoryField } from 'common/enums/data.enum';
import { validateInputString, validateMaxLength, validateNumbers } from 'common/utils/validator';

import SingleSelectMasterData from '../single-select-master-data/SingleSelectMasterData';
import { ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE } from 'modules/configuration/config';

import './AddAndMoveSubCategoryPopup.scss';

type AddAndMoveSubCategoryPopupProps = {
    isVisible: boolean;
    fieldErrors: Array<FieldError>;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    updateSelectedRadioButton: (data: any) => void;
    closePopupHandler: () => void;
    onBackButtonClicked: () => void;
    entityId: string;
    entityType: EntityType;
    hierarchicalData: MasterDataEntity[];
    toggleExpansion: (payload: EntityVM) => void;
    saveHandler: (createNew: string, data: any) => void;
    fetchNextHierachySet: () => void;
};
export default function AddAndMoveSubCategoryPopup({
    isVisible,
    fieldErrors,
    updateFieldErrors,
    updateSelectedRadioButton,
    closePopupHandler,
    onBackButtonClicked,
    hierarchicalData,
    toggleExpansion,
    saveHandler,
    fetchNextHierachySet
}: AddAndMoveSubCategoryPopupProps) {
    const [name, setName] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [parentId, setParentId] = useState<string>('');
    const [isShowPopup, setShowPopup] = useState(false);
    const [targetModelData, setTargetModelData] = useState<any>();

    const handleRadioClicked = (data: any) => {
        setParentId(data.id);
        updateSelectedRadioButton(data);
        setTargetModelData(data);
    };

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Description);
        if (validateMaxLength(event.target.value, SUB_CATEGORY_DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }
        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Name);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                SUB_CATEGORY_NAME_EMPTY_ERROR
            );
        }
        if (validateMaxLength(event.target.value, SUB_CATEGORY_NAME_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                SUB_CATEGORY_NAME_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                SUB_CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.SortOrder);
        if (value !== '') {
            if (!validateNumbers(value)) {
                errors = addValidationError(
                    errors,
                    SubCategoryField.SortOrder,
                    SUB_CATEGORY_ORDER_INVALID_ERROR
                );
            } else {
                if (Number(value) === 0) {
                    errors = addValidationError(
                        errors,
                        SubCategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_GREATER_THAN_ZERO
                    );
                } else if (Number(value) > SUB_CATEGORY_ORDER_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        SubCategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
                    );
                }
            }
        } else {
            errors = addValidationError(
                errors,
                SubCategoryField.SortOrder,
                SUB_CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        updateFieldErrors(errors);
        setSortOrder(value);
    };

    const sortOrderList = getSortOrderList(getHierarchicalData().length);

    const saveSubCategoryHandler = () => {
        let errors = fieldErrors;
        if (!name.trim()) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                SUB_CATEGORY_NAME_EMPTY_ERROR
            );
        }
        if (!sortOrder) {
            errors = addValidationError(
                errors,
                SubCategoryField.SortOrder,
                SUB_CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        if (errors.length === 0) {
            if (parentId !== '') {
                saveHandler('createNewSubCategory', {
                    targetModelData: targetModelData,
                    data: {
                        catName: '',
                        catOrder: '',
                        catDescription: '',
                        subCatName: name,
                        subCatOrder: sortOrder,
                        subCatDescription: description
                    }
                });
                resetStateVariables();
                closePopupHandler();
            } else {
                /**
                 * Error messsage should go here
                 */
                setShowPopup(true);
            }
        }
        updateFieldErrors(errors);
    };

    /**
     * Reset the state variable.
     */
    const resetStateVariables = () => {
        setParentId('');
        setName('');
        setSortOrder('');
        setDescription('');
    };

    /**
     * Function will called on click of CANCEL button.
     */
    const onclickCancelButton = () => {
        resetStateVariables();

        closePopupHandler();

        updateFieldErrors([]);
    };

    /**
     * Function will be called on click of BACK button.
     */
    const onBackButtonClickedLcal = () => {
        resetStateVariables();

        onBackButtonClicked();

        updateFieldErrors([]);
    };

    const onclosePopup = () => {
        resetStateVariables();

        closePopupHandler();

        updateFieldErrors([]);
    };

    return (
        <>
            <Modal show={isVisible} onClose={onclosePopup} style={{ width: '70vw' }}>
                <ModalHeader>Move: Create Sub-Category</ModalHeader>
                <ModalBody>
                    <div className="add-and-move-add-sub-category-container">
                        <div className="add-and-move-add-sub-category-container__left-Container">
                            <Label text={ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE}></Label>
                            <SingleSelectMasterData
                                hideSelectionOnCategory={false}
                                hierarchicalData={hierarchicalData}
                                updateSelectedRadioButton={handleRadioClicked}
                                toggleExpansion={toggleExpansion}
                                hideChildIndicatorsOnly={true}
                                preventSelectionOfSubCategoriesHavingIndicators={true}
                                fetchNextHierachySet={fetchNextHierachySet}
                            />
                        </div>
                        <div className="add-sub-category-container__right-Container">
                            <Label text="Fill in the below form"></Label>

                            <div className="subcategory-form">
                                <FormField className="subcategory-form-field">
                                    <Label text="Subcategory name (required)"></Label>
                                    <Input
                                        type={InputType.TEXT}
                                        value={name}
                                        onChange={handleNameChange}
                                    />
                                    <FieldErrorList
                                        field={SubCategoryField.Name}
                                        errors={fieldErrors}
                                    />
                                </FormField>
                                <FormField className="subcategory-form-field">
                                    <Label text="Sort Order (required)"></Label>
                                    <Input
                                        type={InputType.TEXT}
                                        value={sortOrder}
                                        onChange={(event: any) =>
                                            handleSortOrderChange(event.target.value)
                                        }
                                        onBlur={(event: any) =>
                                            handleSortOrderChange(event.target.value)
                                        }
                                    />

                                    <FieldErrorList
                                        field={SubCategoryField.SortOrder}
                                        errors={fieldErrors}
                                    />
                                </FormField>
                                <FormField className="subcategory-form-field">
                                    <Label text="Description"></Label>
                                    <TextArea value={description} onChange={handleDescription} />
                                    <FieldErrorList
                                        field={SubCategoryField.Description}
                                        errors={fieldErrors}
                                        data-testid="error"
                                    />
                                </FormField>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                            width: '100%'
                        }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'left',
                                flexDirection: 'row',
                                width: '50%'
                            }}>
                            <Button size="medium" type="button" onClick={saveSubCategoryHandler}>
                                {ACTION_ITEMS.SAVE_BUTTON}
                            </Button>
                            <Button
                                size="medium"
                                variant="secondary"
                                type="button"
                                onClick={onclickCancelButton}>
                                {ACTION_ITEMS.CANCEL_BUTTON}
                            </Button>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'right',
                                flexDirection: 'row',
                                width: '50%'
                            }}>
                            <Button
                                variant={ButtonVariant.Secondary}
                                onClick={onBackButtonClickedLcal}>
                                {ACTION_ITEMS.BACK_BUTTON}
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
            {
                <MessageModal
                    headerName={'Warning'}
                    message={'Please choose one category or subcategory!'}
                    isVisible={isShowPopup}
                    isSecondaryButtonVisible={false}
                    type={MessageType.Warning}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={() => {
                        setShowPopup(false);
                    }}
                    onSecondaryAction={() => {
                        setShowPopup(false);
                    }}
                />
            }
        </>
    );
}
